import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getIapMonitor,
  rescueAppleByTransaction,
  rescueAppleManual,
} from "../api/adminApi";
import { StatCard, Section, fmtDate } from "../components/admin/AdminUI";

/**
 * P0 Hotfix (2026-07-08) — Apple IAP Monitor + Rescue console.
 *
 * Surfaces the last-N-hours verify/restore success/fail buckets, env split,
 * failed OTIDs / transaction candidates, and provides 1-click rescue paths.
 */
export default function AdminIapMonitorPage() {
  const [hours, setHours] = useState(24);
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "iap-monitor", hours],
    queryFn: () => getIapMonitor(hours),
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  const [rescueBusy, setRescueBusy] = useState(false);
  const [rescueForm, setRescueForm] = useState({
    user_email: "",
    original_transaction_id: "",
  });
  const [manualForm, setManualForm] = useState({
    user_email: "",
    days: 30,
    reason: "apple_iap_manual_rescue",
  });

  const rescueByTxMutation = useMutation({
    mutationFn: rescueAppleByTransaction,
    onSuccess: (resp) => {
      toast.success(
        `Rescue OK — user is now ${resp.is_pro ? "PRO" : resp.tier}`,
        { description: `Product: ${resp.source_product_id || "n/a"} · Renewal: ${resp.current_period_end || "n/a"}` },
      );
      qc.invalidateQueries({ queryKey: ["admin", "iap-monitor"] });
    },
    onError: (e) => {
      toast.error("Rescue failed", { description: e?.response?.data?.error?.message || String(e) });
    },
    onSettled: () => setRescueBusy(false),
  });

  const rescueManualMutation = useMutation({
    mutationFn: rescueAppleManual,
    onSuccess: (resp) => {
      toast.success(`Manual Pro granted — is_pro=${resp.is_pro}`);
      qc.invalidateQueries({ queryKey: ["admin", "iap-monitor"] });
    },
    onError: (e) => {
      toast.error("Manual rescue failed", { description: e?.response?.data?.error?.message || String(e) });
    },
    onSettled: () => setRescueBusy(false),
  });

  const doRescueByTx = () => {
    if (!rescueForm.user_email.trim() || !rescueForm.original_transaction_id.trim()) {
      toast.warning("Both user_email and original_transaction_id are required.");
      return;
    }
    setRescueBusy(true);
    rescueByTxMutation.mutate({
      user_email: rescueForm.user_email.trim(),
      original_transaction_id: rescueForm.original_transaction_id.trim(),
    });
  };

  const doRescueManual = () => {
    if (!manualForm.user_email.trim()) {
      toast.warning("user_email is required.");
      return;
    }
    setRescueBusy(true);
    rescueManualMutation.mutate({
      user_email: manualForm.user_email.trim(),
      days: Math.max(1, Math.min(365, Number(manualForm.days) || 30)),
      reason: manualForm.reason.trim() || "apple_iap_manual_rescue",
    });
  };

  const rescueOtid = (otid) => {
    setRescueForm((f) => ({ ...f, original_transaction_id: otid || "" }));
    // scroll rescue card into view
    document.getElementById("iap-rescue-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const rescueByEmail = (email) => {
    setRescueForm((f) => ({ ...f, user_email: email || "" }));
    setManualForm((f) => ({ ...f, user_email: email || "" }));
    document.getElementById("iap-rescue-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) return <div className="text-zinc-500">Loading IAP monitor…</div>;
  if (isError) {
    return (
      <div className="text-rose-400" data-testid="iap-monitor-error">
        Error: {String(error?.message || error)}
      </div>
    );
  }
  if (!data) return null;

  const verify = data.verify || { success: 0, failed: 0, env: {} };
  const restore = data.restore || { success: 0, failed: 0, env: {} };
  const rescueCounts = data.rescue || { by_tx: 0, manual: 0 };
  const totalVerify = (verify.success || 0) + (verify.failed || 0);
  const totalRestore = (restore.success || 0) + (restore.failed || 0);
  const verifyFailPct = totalVerify > 0 ? Math.round((verify.failed / totalVerify) * 100) : 0;
  const restoreFailPct = totalRestore > 0 ? Math.round((restore.failed / totalRestore) * 100) : 0;

  return (
    <div data-testid="admin-iap-monitor-page" className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            /api/admin/apple/iap-monitor
          </span>
          <h1 className="text-2xl text-zinc-100 font-medium mt-1">
            Apple IAP monitor
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Verify + restore counts over the last <b className="text-zinc-300">{data.window_hours}h</b>. Auto-refresh every 60 s.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            data-testid="iap-monitor-hours"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-2 py-1"
          >
            <option value={1}>1 h</option>
            <option value={6}>6 h</option>
            <option value={24}>24 h</option>
            <option value={72}>72 h</option>
            <option value={168}>7 d</option>
          </select>
          <button
            data-testid="iap-monitor-refresh"
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-3 py-1 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-200 text-sm hover:bg-zinc-800 disabled:opacity-50"
          >
            {isFetching ? "…" : "Refresh"}
          </button>
        </div>
      </header>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Verify · Success"
          value={verify.success}
          hint={`${totalVerify} total`}
          testid="iap-stat-verify-success"
        />
        <StatCard
          label="Verify · Failed"
          value={verify.failed}
          hint={`${verifyFailPct}% fail rate`}
          testid="iap-stat-verify-failed"
        />
        <StatCard
          label="Restore · Success"
          value={restore.success}
          hint={`${totalRestore} total`}
          testid="iap-stat-restore-success"
        />
        <StatCard
          label="Restore · Failed"
          value={restore.failed}
          hint={`${restoreFailPct}% fail rate`}
          testid="iap-stat-restore-failed"
        />
      </div>

      {/* Env split + rescue counts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Environment split (verified success)" testid="iap-env-split">
          <div className="grid grid-cols-3 text-xs text-zinc-300">
            <div>
              <div className="text-[10px] uppercase text-emerald-400 tracking-wider">Verify</div>
              <div>prod: <span className="tabular-nums text-emerald-200">{verify.env?.prod || 0}</span></div>
              <div>sandbox: <span className="tabular-nums text-amber-300">{verify.env?.sandbox || 0}</span></div>
              <div>unknown: <span className="tabular-nums text-zinc-500">{verify.env?.unknown || 0}</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-emerald-400 tracking-wider">Restore</div>
              <div>prod: <span className="tabular-nums text-emerald-200">{restore.env?.prod || 0}</span></div>
              <div>sandbox: <span className="tabular-nums text-amber-300">{restore.env?.sandbox || 0}</span></div>
              <div>unknown: <span className="tabular-nums text-zinc-500">{restore.env?.unknown || 0}</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-emerald-400 tracking-wider">Rescues</div>
              <div>by transaction: <span className="tabular-nums text-cyan-300">{rescueCounts.by_tx || 0}</span></div>
              <div>manual: <span className="tabular-nums text-cyan-300">{rescueCounts.manual || 0}</span></div>
            </div>
          </div>
        </Section>

        <Section title="Health check" testid="iap-health-check">
          <div className="text-xs text-zinc-300 space-y-2">
            <div>
              <span className="text-zinc-500">Verify fail rate:</span>{" "}
              <b className={verifyFailPct > 20 ? "text-rose-300" : "text-emerald-300"}>{verifyFailPct}%</b>
            </div>
            <div>
              <span className="text-zinc-500">Restore fail rate:</span>{" "}
              <b className={restoreFailPct > 20 ? "text-rose-300" : "text-emerald-300"}>{restoreFailPct}%</b>
            </div>
            <div>
              <span className="text-zinc-500">Sandbox verifies:</span>{" "}
              <b className={verify.env?.sandbox > 0 ? "text-amber-300" : "text-zinc-400"}>
                {verify.env?.sandbox || 0}
              </b>{" "}
              <span className="text-zinc-500">
                {verify.env?.sandbox > 0 ? "· expected only for TestFlight/dev" : ""}
              </span>
            </div>
          </div>
        </Section>
      </div>

      {/* Recent failures — rescue candidates */}
      <Section title="Recent failures — rescue candidates" testid="iap-recent-failures">
        {data.recent_failures?.length === 0 ? (
          <div className="text-xs text-zinc-500">No failures in this window. 🎉</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Endpoint</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">OTID / TX</th>
                  <th className="text-right px-3 py-2">Rescue</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {data.recent_failures.map((row, idx) => {
                  const otid = row.original_transaction_id || row.transaction_id || "";
                  return (
                    <tr key={idx} className="border-t border-zinc-900/60 hover:bg-zinc-900/30">
                      <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${
                          row.endpoint === "verify"
                            ? "bg-cyan-500/10 text-cyan-200 border border-cyan-500/40"
                            : "bg-purple-500/10 text-purple-200 border border-purple-500/40"
                        }`}>{row.endpoint}</span>
                      </td>
                      <td className="px-3 py-2 text-rose-300">{row.reason}</td>
                      <td className="px-3 py-2">
                        {row.user_email ? (
                          <button
                            data-testid={`iap-failure-email-btn-${idx}`}
                            onClick={() => rescueByEmail(row.user_email)}
                            className="text-emerald-300 hover:underline"
                          >
                            {row.user_email}
                          </button>
                        ) : (
                          <span className="text-zinc-500 font-mono text-[10px]">{row.user_ulid || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">
                        {otid || <span className="text-zinc-600">n/a</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {otid ? (
                          <button
                            data-testid={`iap-failure-rescue-btn-${idx}`}
                            onClick={() => rescueOtid(otid)}
                            className="text-xs px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          >
                            Rescue →
                          </button>
                        ) : (
                          <span className="text-zinc-600 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Recent rescues */}
      <Section title="Recent admin rescues" testid="iap-recent-rescues">
        {data.recent_rescues?.length === 0 ? (
          <div className="text-xs text-zinc-500">No admin rescues in this window.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Actor</th>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {data.recent_rescues.map((row, idx) => (
                  <tr key={idx} className="border-t border-zinc-900/60 hover:bg-zinc-900/30">
                    <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-cyan-500/10 text-cyan-200 border border-cyan-500/40">
                        {row.event_type.replace("apple_admin_rescue_", "")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{row.actor_admin_email || "—"}</td>
                    <td className="px-3 py-2 text-emerald-300">{row.target_user_email || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">
                      {row.original_transaction_id || row.reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Rescue console */}
      <Section title="Rescue console" testid="iap-rescue-card">
        <div id="iap-rescue-card" className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rescue by transaction */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm text-zinc-200 font-medium">Rescue by transaction</h3>
              <span className="font-mono text-[10px] text-emerald-400">POST /admin/apple/rescue-by-transaction</span>
            </div>
            <p className="text-xs text-zinc-500">
              Pull the transaction from Apple&apos;s Server API + bind to the target user. Full audit trail.
            </p>
            <label className="block text-xs text-zinc-400 space-y-1">
              User email
              <input
                data-testid="iap-rescue-tx-email"
                type="email"
                value={rescueForm.user_email}
                onChange={(e) => setRescueForm((f) => ({ ...f, user_email: e.target.value }))}
                placeholder="user@example.com"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-100"
              />
            </label>
            <label className="block text-xs text-zinc-400 space-y-1">
              Original Transaction ID (or Transaction ID)
              <input
                data-testid="iap-rescue-tx-otid"
                type="text"
                value={rescueForm.original_transaction_id}
                onChange={(e) => setRescueForm((f) => ({ ...f, original_transaction_id: e.target.value }))}
                placeholder="2000000123456789"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm font-mono text-zinc-100"
              />
            </label>
            <button
              data-testid="iap-rescue-tx-submit"
              onClick={doRescueByTx}
              disabled={rescueBusy}
              className="w-full px-3 py-2 rounded-md bg-emerald-500/20 text-emerald-100 border border-emerald-500/50 hover:bg-emerald-500/30 text-sm disabled:opacity-50"
            >
              {rescueBusy ? "Rescuing…" : "Rescue user via Apple"}
            </button>
          </div>

          {/* Manual rescue */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-950/10 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm text-amber-200 font-medium">Manual grant (nuclear)</h3>
              <span className="font-mono text-[10px] text-amber-400">POST /admin/apple/rescue-manual</span>
            </div>
            <p className="text-xs text-amber-100/60">
              Bypasses Apple&apos;s Server API entirely. Grants Pro via <code className="font-mono text-amber-200">pro_access_until</code>. Use only if the API path is broken.
            </p>
            <label className="block text-xs text-zinc-400 space-y-1">
              User email
              <input
                data-testid="iap-rescue-manual-email"
                type="email"
                value={manualForm.user_email}
                onChange={(e) => setManualForm((f) => ({ ...f, user_email: e.target.value }))}
                placeholder="user@example.com"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-100"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-zinc-400 space-y-1">
                Days (1-365)
                <input
                  data-testid="iap-rescue-manual-days"
                  type="number"
                  min={1}
                  max={365}
                  value={manualForm.days}
                  onChange={(e) => setManualForm((f) => ({ ...f, days: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-100"
                />
              </label>
              <label className="block text-xs text-zinc-400 space-y-1">
                Reason
                <input
                  data-testid="iap-rescue-manual-reason"
                  type="text"
                  value={manualForm.reason}
                  onChange={(e) => setManualForm((f) => ({ ...f, reason: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-zinc-100"
                />
              </label>
            </div>
            <button
              data-testid="iap-rescue-manual-submit"
              onClick={doRescueManual}
              disabled={rescueBusy}
              className="w-full px-3 py-2 rounded-md bg-amber-500/20 text-amber-100 border border-amber-500/50 hover:bg-amber-500/30 text-sm disabled:opacity-50"
            >
              {rescueBusy ? "Granting…" : "Grant Pro manually"}
            </button>
          </div>
        </div>
      </Section>

      <div className="text-[10px] text-zinc-600 font-mono">
        generated_at: {fmtDate(data.generated_at)}
      </div>
    </div>
  );
}
